import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Map slot names to their ad format positions on /advertise
const SLOT_TO_ADVERTISE_MAP = {
  'leaderboard-top': 'Leaderboard (970x90)',
  'sidebar-top': 'Sidebar Top (300x250)',
  'sidebar-bottom': 'Sidebar Bottom (300x250)',
  'sidebar-skyscraper': 'Skyscraper (300x600)',
  'sidebar-category-top': 'Sidebar Category (300x250)',
  'sidebar-category-bottom': 'Sidebar Category Bottom (300x250)',
  'in-feed-1': 'In-Feed Banner (728x90)',
  'bottom-leaderboard': 'Bottom Leaderboard (728x90)',
  'in-content-1': 'In-Content (728x90)',
  'post-content': 'Post-Content (728x90)',
  'special-header-landing-ad': 'Special Header Landing Ad (300x250)',
  'sidebar-after-horoscope': 'Sidebar After Horoscope (300x250)',
  'sidebar-after-zse': 'Sidebar After ZSE (300x250)',
  'sidebar-after-ferry': 'Sidebar After Ferry (300x250)',
  'sidebar-after-airquality': 'Sidebar After Air Quality (300x250)',
  'sidebar-after-comments': 'Sidebar After Comments (300x250)',
};

/**
 * Smart ad slot that:
 * 1. Checks for AdSense code configured for this slot
 * 2. Checks for a custom ad (image + URL) for this slot
 * 3. Falls back to a placeholder linking to /advertise
 */
export default function AdSlot({ width = '728', height = '90', slot = '', className = '' }) {
  const { t } = useLanguage();
  const [adConfig, setAdConfig] = useState(null);

  useEffect(() => {
    if (!slot) return;
    // Try to load slot-specific ad config from backend
    fetch(`${API}/api/ad-slot-config/${slot}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAdConfig(data); })
      .catch(() => {});
  }, [slot]);

  // If there's an AdSense code for this slot, render it
  if (adConfig?.adsense_code) {
    return (
      <div
        className={`ad-slot ${className}`}
        style={{ width: '100%', maxWidth: `${width}px` }}
        data-testid={`ad-slot-${slot}`}
        data-ad-slot={slot}
        role="complementary"
        aria-label="Advertisement"
        dangerouslySetInnerHTML={{ __html: adConfig.adsense_code }}
      />
    );
  }

  // If there's a custom ad image with target URL
  if (adConfig?.image_url) {
    return (
      <div
        className={`ad-slot ${className}`}
        style={{ width: '100%', maxWidth: `${width}px` }}
        data-testid={`ad-slot-${slot}`}
        data-ad-slot={slot}
        role="complementary"
        aria-label="Advertisement"
      >
        <a href={adConfig.target_url || '/advertise'} target="_blank" rel="noopener noreferrer">
          <img
            src={adConfig.image_url}
            alt="Advertisement"
            style={{ width: '100%', maxWidth: `${width}px`, height: 'auto', maxHeight: `${height}px`, objectFit: 'contain' }}
            className="rounded"
          />
        </a>
      </div>
    );
  }

  // Default: placeholder linking to /advertise
  const advertiseParam = SLOT_TO_ADVERTISE_MAP[slot] || slot;
  return (
    <Link
      to={`/advertise`}
      className={`ad-slot block ${className}`}
      style={{
        width: '100%',
        maxWidth: `${width}px`,
        height: `${height}px`,
        aspectRatio: `${width}/${height}`,
      }}
      data-testid={`ad-slot-${slot}`}
      data-ad-slot={slot}
      role="complementary"
      aria-label="Advertisement"
      title={`Oglasite se ovdje — ${advertiseParam}`}
    >
      <span className="text-gray-300 dark:text-gray-600 font-display text-[10px] mt-3">{width} x {height}</span>
      <span className="text-[9px] text-gray-300 dark:text-gray-600 block">Oglasite se ovdje</span>
    </Link>
  );
}
